/**
 * PasswordInput — input mot de passe avec toggle "afficher / masquer".
 *
 * Pattern moderne (Stripe, Notion, Linear) : un seul champ + œil cliquable
 * pour vérifier la saisie en cas de doute. Combiné à un champ "confirmation"
 * dans les formulaires signup/reset, cumule deux protections anti-typo.
 *
 * Compatible react-hook-form via `forwardRef` : on peut passer directement
 * `<PasswordInput {...register("mot_de_passe")} />` et RHF gère value/onChange/ref.
 * Les props HTML standard (autoComplete, minLength, maxLength, autoFocus,
 * placeholder, aria-*) sont transmises au `<input>` sous-jacent.
 */

import { Eye, EyeOff } from "lucide-react";
import { forwardRef, useState, type InputHTMLAttributes } from "react";

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** True si le champ est en erreur — applique une bordure rouge. */
  hasError?: boolean;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ hasError = false, className, ...rest }, ref) {
    const [show, setShow] = useState(false);

    return (
      <div className="relative">
        <input
          {...rest}
          ref={ref}
          type={show ? "text" : "password"}
          className={[
            "block h-12 w-full rounded-xl border bg-white px-4 pr-12 text-base text-champagne-900",
            "placeholder:text-champagne-800/30 focus:outline-none focus:ring-2",
            hasError
              ? "border-red-600 focus:border-red-700 focus:ring-red-600/20"
              : "border-champagne-800/20 focus:border-champagne-500 focus:ring-champagne-500/20",
            className ?? "",
          ].join(" ")}
        />
        <button
          type="button"
          onClick={() => setShow((prev) => !prev)}
          aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
          aria-pressed={show}
          // tabIndex=-1 : on saute ce bouton dans la navigation clavier (l'utilisateur
          // qui tab depuis le password va directement au champ suivant). L'œil reste
          // accessible au clic souris/tactile et via shortcut clavier (Tab + Shift).
          tabIndex={-1}
          className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-champagne-800/70 transition hover:bg-champagne-50 hover:text-champagne-900 focus:outline-none focus:ring-2 focus:ring-champagne-500/30"
        >
          {show ? (
            <EyeOff size={20} strokeWidth={1.6} aria-hidden="true" />
          ) : (
            <Eye size={20} strokeWidth={1.6} aria-hidden="true" />
          )}
        </button>
      </div>
    );
  },
);
